/**
 * Browser `WebSocketLike` transport — adapts the DOM `WebSocket` to the
 * core's seam (P0b §3.3). DOM-typed, so ONLY browser surfaces import it (the
 * `.` core barrel does NOT re-export it, keeping the core node-consumable;
 * import via `@volley/crucible-jsonrpc-core/browser`).
 *
 * Auth attachment (TDD §3.4): the browser can only set the WebSocket
 * subprotocol on the upgrade, never headers — so it honours `cookie` (no
 * subprotocol; the same-origin HttpOnly session cookie travels automatically)
 * and `subprotocol-jwt` (the `crucible-auth.<jwt>` subprotocol), and throws for
 * the header-bearing kinds rather than silently dropping the credential.
 */

import {
  type AuthAttachment,
  type WebSocketLike,
  type WebSocketTransportFactory,
} from '../websocket-like.js';

/** A minimal structural view of the DOM `WebSocket` ctor — injectable for tests. */
export interface BrowserWebSocketCtor {
  new (url: string, protocols?: string | string[]): WebSocket;
}

export interface BrowserTransportDeps {
  /** Defaults to the global `WebSocket`. Tests inject a fake. */
  webSocketCtor?: BrowserWebSocketCtor;
}

/** Translate an `AuthAttachment` to the browser upgrade subprotocols, or throw. */
export function browserAuthProtocols(auth: AuthAttachment): string[] {
  switch (auth.kind) {
    case 'cookie':
      return [];
    case 'subprotocol-jwt':
      return [`crucible-auth.${auth.token}`];
    case 'header-bearer':
    case 'stub':
    case 'headers':
      throw new Error(
        `browser transport cannot attach auth kind '${auth.kind}' — browsers cannot set WebSocket upgrade headers; use cookie or subprotocol-jwt`,
      );
  }
}

export function createBrowserWebSocketTransport(
  deps: BrowserTransportDeps = {},
): WebSocketTransportFactory {
  const Ctor = deps.webSocketCtor ?? (WebSocket as unknown as BrowserWebSocketCtor);
  return (url, auth) => new BrowserWebSocketAdapter(new Ctor(url, browserAuthProtocols(auth)));
}

class BrowserWebSocketAdapter implements WebSocketLike {
  onopen: (() => void) | null = null;
  onmessage: ((data: string) => void) | null = null;
  onclose: ((ev: { code: number; reason: string }) => void) | null = null;
  onerror: (() => void) | null = null;

  constructor(private readonly ws: WebSocket) {
    ws.addEventListener('open', () => this.onopen?.());
    ws.addEventListener('message', (e: MessageEvent) => {
      // JSON-RPC frames are text; a binary frame (Blob/ArrayBuffer) is not part
      // of the protocol surface — coerce defensively to a string so the core's
      // JSON.parse fails loudly via its malformed-frame diagnostic rather than
      // throwing here.
      this.onmessage?.(typeof e.data === 'string' ? e.data : String(e.data));
    });
    ws.addEventListener('close', (e: CloseEvent) =>
      this.onclose?.({ code: e.code, reason: e.reason }),
    );
    ws.addEventListener('error', () => this.onerror?.());
  }

  get readyState(): number {
    return this.ws.readyState;
  }
  send(data: string): void {
    this.ws.send(data);
  }
  close(code?: number, reason?: string): void {
    this.ws.close(code, reason);
  }
}
